import { isEmpty } from 'lodash';
import { useAsyncFn } from 'react-use';

import * as Sentry from 'lib/client/sentry/sentry';
import { postRequestOptions } from 'components/ContentReporting/form-utils';
import { getValidSpotifyUriList, isSpotifyUri } from 'components/ContentReporting/validation-utils';
import { CONTENTREPORTINGAPI_URL } from 'containers/ContentReporting/ContentPolicyReporting/utils/constants';
import { couldHaveAssociatedContent } from 'containers/ContentReporting/ContentLegalReporting/utils/functions/contentType';
import { CONTENT_TYPE } from 'containers/ContentReporting/ContentLegalReporting/utils/types';
import { ASSOCIATED_CONTENT_ERRORS } from 'containers/ContentReporting/ContentLegalReporting/utils/constants';
import {
  AssociatedContent,
  AssociatedContentResponse,
  entityTypeFromJSON,
  MultipleAssociatedContentResponse,
} from 'proto/generated/inspector_gadget';

import { useEntityTypeFn } from './useEntityTypeFn';

type useAssociatedContentParams = { contentType?: CONTENT_TYPE };

const DEFAULT_RESPONSE = { associatedContent: [], assetIds: [], associatedContentElements: [] };

export const useAssociatedContent = (params: useAssociatedContentParams) => {
  const { contentType } = params;
  const { fetchEntityType } = useEntityTypeFn();

  const [
    {
      value: { associatedContent, assetIds, associatedContentElements } = {
        associatedContent: [],
        assetIds: [],
        associatedContentElements: [],
      },
      loading,
      error,
    },
    fetchAssociatedContent,
  ] = useAsyncFn(
    async (
      reportedUris: string[],
    ): Promise<{
      associatedContent: AssociatedContentResponse[];
      assetIds: string[];
      associatedContentElements: AssociatedContent[];
    }> => {
      const uris = getValidSpotifyUriList(Array.from(new Set(reportedUris.filter(isSpotifyUri))));

      if (isEmpty(uris)) {
        return DEFAULT_RESPONSE;
      }

      const resolvedEntityType = await fetchEntityType(uris);
      const resolvedEntityTypes = resolvedEntityType.map(({ entityType }) => entityTypeFromJSON(entityType));

      if (!(contentType && couldHaveAssociatedContent(contentType, resolvedEntityTypes))) {
        return DEFAULT_RESPONSE;
      }

      const overwritteContentType = contentType === CONTENT_TYPE.ARTWORK ? 'album_episode_artwork' : contentType;

      const options = postRequestOptions({
        uris: uris,
        associatedContentType: overwritteContentType,
      });

      try {
        const response = await fetch(`${CONTENTREPORTINGAPI_URL}/v1/multiple-associated-content`, options);
        if (response.ok) {
          const { associatedContent: urisAssociatedContent }: MultipleAssociatedContentResponse = await response.json();

          const newAssociatedContentElements = urisAssociatedContent.map(({ elements }) => elements).flat();
          const newAssetIds = newAssociatedContentElements.map(({ value }) => value);

          return {
            associatedContent: urisAssociatedContent,
            assetIds: newAssetIds,
            associatedContentElements: newAssociatedContentElements,
          };
        }

        Sentry.captureException(
          `Called "/v1/multiple-associated-content", Code: ${response.status}; Payload: ${response.body}`,
        );
      } catch (e: unknown) {
        Sentry.captureException(`Error under useAssociatedContent: ${JSON.stringify(e)}`);
      }
      return Promise.reject(ASSOCIATED_CONTENT_ERRORS.GENERIC_ERROR);
    },
    [contentType],
  );

  return {
    isLoading: loading,
    error,
    fetchAssociatedContent,
    associatedContent,
    associatedContentElements,
    assetIds,
  };
};

export default useAssociatedContent;
